import React, { ReactElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import theme from 'live/services/theme'
import authorization from 'live/services/authorization'
import { StaticDataTypeI18nKey, STATIC_DATA_STATES } from '../chart-components'
import {
    CustomMeasureSelect,
    CustomModalsProps,
    GenericWarning,
    ModalField,
    ModalSectionHeader,
    ModalTabSections,
    NumberInput
} from '../modal-components'
import { ModalHeader, ModalBody, ModalFooter, ModalButton, ModalSideBody } from '../modal-components-style'
import { ActivitySeries, RawRunEvent } from '../../utils/type'
import { createSingleEntity, fetchEntityEndTimestamp, RUN_API, TUBULAR_API, updateSingleEntity } from '../../api/api'
import { fetchEntityStartAndNumberTimestamp } from '../../api/runApi'
import { StaticDataContext, useStaticDataWidget } from '../../context/StaticDataContext'
import { getEmptyErrors, emptyRawRunEvent, emptyRunErrors, Errors, RunErrors } from './types'
import RunTab from './RunTab'
import {
    RunModalRegistryService,
    componentExtractor,
    RunTabRegistryService
} from '../../../../../shared/services/StaticDataManagerRegistry'
import { ExtensionPoint } from 'live/components/ui/ExtensionPoint'
import { ScrollModalContainer } from '../activity/ActivityModal'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { regexNumberAndComma } from '../../utils/parse'

const errorMessage = i18n('Please correct the highlighted fields')
interface RunModalProps extends CustomModalsProps {
    event: RawRunEvent
    closeModal: (runData?: any, action?: string) => void
    isSharedComponent: boolean
    setIsSharedComponent: React.Dispatch<React.SetStateAction<boolean>>
    tubulars?: any[]
    setTubulars: React.Dispatch<React.SetStateAction<any[]>>
    isModalDoubleSize: boolean
    setIsModalDoubleSize: React.Dispatch<React.SetStateAction<boolean>>
}

export const RunModal = ({
    title = StaticDataTypeI18nKey.RUN,
    closeModal,
    event,
    isSharedComponent,
    setIsSharedComponent,
    tubulars,
    setTubulars,
    isModalDoubleSize,
    setIsModalDoubleSize
}: RunModalProps): ReactElement => {
    const context = useContext(StaticDataContext)
    const liveTheme = context?.liveTheme

    const hasPermissionToEdit =
        authorization.superAdmin ||
        authorization.admin ||
        authorization.Permission.ADMIN ||
        authorization.Permission.OGPERFORMANCE_MANAGE_KPI ||
        authorization.Permission.OGPERFORMANCE_ADMINISTRATE_KPI

    const { updateWidgetData, widgetData } = useContext(StaticDataContext)

    const { reloadData } = useStaticDataWidget()
    const [runRawEvent, setRunRawEvent] = useState<RawRunEvent>(event?.id ? event : { ...emptyRawRunEvent, ...event })
    const [errors, setErrors] = useState<Errors>(getEmptyErrors())
    const [sharedState, setSharedState] = useState<Record<string, unknown>>({})
    const [cementedPlugPresent, setCementedPlugPresent] = useState<boolean>(runRawEvent?.cementedPlugs?.length > 0)
    const runModalContainerRef = useRef<HTMLDivElement>(null)

    const [selectedEvent, setSelectedEvent] = useState<RawRunEvent>(event)
    const [unitOptions, setUnitOptions] = useState<Uom[]>([])
    const fieldDisabledAfterEntityStart = selectedEvent.state !== STATIC_DATA_STATES.EMPTY

    const isTransitionOrNotTubular = ['NON_TUBULAR_RUN', 'TRANSITION'].includes(selectedEvent.type)

    const emptyCementedPlug = useMemo(
        () => ({
            startMD: { value: null, uom: unitOptions?.find(elem => elem.name == 'm') || unitOptions?.[0] || null },
            finalMD: { value: null, uom: unitOptions?.find(elem => elem.name == 'm') || unitOptions?.[0] || null }
        }),
        [unitOptions]
    )

    const setDefaultUnits = (): void => {
        const defaultLengthUnit = unitOptions.find(elem => elem?.name == 'm') || unitOptions[0]
        setSelectedEvent(prev => ({
            ...prev,
            startMD: {
                ...prev.startMD,
                uom: prev.startMD?.uom || defaultLengthUnit
            },
            finalMD: {
                ...prev.finalMD,
                uom: prev.finalMD?.uom || defaultLengthUnit
            }
        }))
    }

    const updateRunEvent = useCallback((newEvent: RawRunEvent) => {
        setRunRawEvent(prev => ({ ...prev, ...newEvent }))
    }, [])

    const postRun = (rawRun: RawRunEvent): any => {
        const runData = (({ state, ...rawRun }) => rawRun)(rawRun)

        return (rawRun.id ? updateSingleEntity : createSingleEntity)(RUN_API, runData)
    }

    const addNewCementedPlug = () => {
        if (!fieldDisabledAfterEntityStart) {
            setSelectedEvent(prevstate => ({
                ...prevstate,
                cementedPlugs: [...(prevstate?.cementedPlugs ?? []), emptyCementedPlug]
            }))
        }
    }

    useEffect(() => {
        if (isTransitionOrNotTubular) {
            setSelectedEvent(prevstate => ({
                ...prevstate,
                tubularsIntervalPerTime: isTransitionOrNotTubular,
                startMD: { value: null, uom: prevstate.startMD?.uom },
                finalMD: { value: null, uom: prevstate.finalMD?.uom }
            }))
        } else {
            setDefaultUnits()
        }
    }, [isTransitionOrNotTubular])

    useEffect(() => {
        if (event.id) {
            fetchEntityEndTimestamp(event.id, RUN_API, TUBULAR_API).then(entityEnd => {
                if (entityEnd) setRunRawEvent(prevstate => ({ ...prevstate, end: entityEnd }))
            })
        } else {
            fetchEntityStartAndNumberTimestamp(event.section_id).then(timestamp => {
                const { start, number } = timestamp

                setRunRawEvent(prevstate => ({
                    ...prevstate,
                    start: Number(start),
                    number: Number(number) + 1
                }))
            })
        }
    }, [event])

    const updateFinalMDValueOfLastTubular = (runRawEvent: any) => {
        const fieldsData: ActivitySeries = { ...context?.widgetData?.activitySeries }
        const validTubulars = fieldsData?.data
            ?.filter(x => x?.rawEvent?.id && x.rawEvent?.run_id == runRawEvent.id)
            ?.sort((a, b) => b?.rawEvent?.id - a?.rawEvent?.id)
        const lastTubular = validTubulars[0]?.rawEvent
        if (lastTubular?.finalMD) {
            lastTubular.finalMD = runRawEvent.finalMD
            const newFieldsData: ActivitySeries = { ...fieldsData }
            const lastTubularIndex = newFieldsData.data.findIndex(y => y?.rawEvent?.id == lastTubular?.id)
            newFieldsData.data[lastTubularIndex].rawEvent = lastTubular
            updateWidgetData('activitySeries', newFieldsData)
            lastTubular.start = null
            lastTubular.end = null
            updateSingleEntity(TUBULAR_API, lastTubular)
        }
    }

    function validateRunFields(event: RawRunEvent): { pass: boolean; errors: RunErrors } {
        const isRunTypeContains = ['NON_TUBULAR_RUN', 'TRANSITION'].includes(event.type)

        let pass = true
        let errors: RunErrors = emptyRunErrors
        if (!event.number) {
            pass = false
            errors = { ...errors, number: true }
        }

        if (!isRunTypeContains) {
            if (isNaN(event.startMD?.value ?? NaN) || !event.startMD?.uom) {
                pass = false
                errors = { ...errors, startMD: true }
            }

            if (event.type !== 'RISER' && (!event.finalMD?.value || !event.finalMD.uom)) {
                pass = false
                errors = { ...errors, finalMD: true }
            }
        }

        if (!event.start) {
            pass = false
            errors = { ...errors, start: true }
        }

        if (!event.type) {
            pass = false
            errors = { ...errors, type: true }
        }

        if (!event?.tubularsIntervalPerTime && !event?.tubularsIntervalPerDepth) { 
            pass = false
            errors = { ...errors, tubularsInterval: true }
        }

        return {
            pass: pass,
            errors: errors
        }
    }

    function validateFields(rawRunEvent: RawRunEvent): { pass: boolean; errors: Errors } {
        const errors: Errors = getEmptyErrors()
        const { pass: passRun, errors: runErrors } = validateRunFields(rawRunEvent)
        if (!passRun) {
            errors.messages = {
                ...errors.messages,
                run: errorMessage
            }
            errors.run = runErrors
        }

        return {
            pass: passRun,
            errors: errors
        }
    }

    const btn =
        event.state != STATIC_DATA_STATES.EMPTY
            ? event.state !== STATIC_DATA_STATES.FINISHED
                ? 'Finish'
                : 'Save'
            : 'Start'

    const props = {
        title,
        closeModal,
        event,
        isSharedComponent,
        setIsSharedComponent,
        tubulars,
        setTubulars,
        isModalDoubleSize,
        setIsModalDoubleSize,
        context,
        hasPermissionToEdit,
        updateWidgetData,
        widgetData,
        reloadData,
        runRawEvent,
        setRunRawEvent,
        errors,
        setErrors,
        updateRunEvent,
        postRun,
        updateFinalMDValueOfLastTubular,
        validateRunFields,
        validateFields,
        btn,
        sharedState,
        setSharedState,
        runModalContainer: runModalContainerRef.current,
        cementedPlugPresent,
        setCementedPlugPresent,
        selectedEvent,
        setSelectedEvent,
        unitOptions,
        setUnitOptions,
        fieldDisabledAfterEntityStart,
        isTransitionOrNotTubular,
        emptyCementedPlug,
        setDefaultUnits,
        addNewCementedPlug
    }

    return (
        <>
            <RunModalContainer ref={runModalContainerRef}>
                <ModalHeader>{i18n(title).toUpperCase()}</ModalHeader>
                <>
                    <ScrollModalContainer>
                        <ModalBody
                            liveTheme={context.liveTheme}
                            id="modal-body"
                            style={{
                                width: '344px',
                                paddingBottom: '22px'
                            }}
                        >
                            <ModalTabSections
                                selected
                                content={
                                    <RunTab
                                        event={runRawEvent}
                                        errors={errors.run}
                                        updateRunEvent={updateRunEvent}
                                        hasPermissionToEdit={hasPermissionToEdit}
                                        isSharedComponent={isSharedComponent}
                                        setIsSharedComponent={setIsSharedComponent}
                                        tubulars={tubulars}
                                        setTubulars={setTubulars}
                                        sharedState={sharedState}
                                        setSharedState={setSharedState}
                                        runModalContainer={runModalContainerRef}
                                        fieldDisabledAfterEntityStart={fieldDisabledAfterEntityStart}
                                        unitOptions={unitOptions}
                                        setUnitOptions={setUnitOptions}
                                        selectedEvent={selectedEvent}
                                        setSelectedEvent={setSelectedEvent}
                                        isTransitionOrNotTubular={isTransitionOrNotTubular}
                                        cementedPlugPresent={cementedPlugPresent}
                                        setCementedPlugPresent={setCementedPlugPresent}
                                        addNewCementedPlug={addNewCementedPlug}
                                        isModalDoubleSize={isModalDoubleSize}
                                        setIsModalDoubleSize={setIsModalDoubleSize}
                                        emptyCementedPlug={emptyCementedPlug}
                                    />
                                }
                            />
                            <GenericWarning message={errors?.messages?.run} />
                        </ModalBody>

                        <ExtensionPoint
                            componentProps={props}
                            strategy={'render-all'}
                            registry={RunModalRegistryService.registry}
                            componentExtractor={componentExtractor}
                        />

                        {isModalDoubleSize && (
                            <div onMouseDown={e => e.stopPropagation()}>
                                <ModalSideBody liveTheme={liveTheme} height={'100%'} leftBorder={true}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: 16,
                                            marginBottom: 16,
                                            borderBottom: '1px solid var(--gray-300)'
                                        }}
                                    >
                                        <ModalSectionHeader label={i18n('Cemented Plug')} />
                                    </div>

                                    <CementContainer
                                        removeDisabledAfterEntityStart={fieldDisabledAfterEntityStart}
                                        showTrash={cementedPlugPresent && selectedEvent.cementedPlugs.length > 1}
                                    >
                                        {(cementedPlugPresent
                                            ? selectedEvent?.cementedPlugs
                                            : [emptyCementedPlug]
                                        )?.map((cementedPlug, cementIndex) => (
                                            <FlexContainer key={cementIndex}>
                                                <ModalField
                                                    label={'Start MD'}
                                                    labelAditionalField={
                                                        <CustomMeasureSelect
                                                            setSelectedUomValue={uomValue =>
                                                                setSelectedEvent(prevstate => ({
                                                                    ...prevstate,
                                                                    cementedPlugs: prevstate?.cementedPlugs?.map(
                                                                        (c, idx) => {
                                                                            if (idx !== cementIndex) return c

                                                                            return {
                                                                                ...cementedPlug,
                                                                                startMD: uomValue
                                                                            }
                                                                        }
                                                                    )
                                                                }))
                                                            }
                                                            selectedOptionValues={cementedPlug?.startMD}
                                                            options={unitOptions}
                                                        />
                                                    }
                                                    field={
                                                        <div className="input-uom">
                                                            <NumberInput
                                                                value={cementedPlug?.startMD?.value}
                                                                disabled={
                                                                    !hasPermissionToEdit ||
                                                                    !cementedPlugPresent ||
                                                                    (fieldDisabledAfterEntityStart &&
                                                                        selectedEvent.type !== 'RISER')
                                                                }
                                                                onChange={e => {
                                                                    if (regexNumberAndComma.test(e?.target?.value)) {
                                                                        e.persist()
                                                                        setSelectedEvent(prevstate => ({
                                                                            ...prevstate,
                                                                            cementedPlugs: prevstate?.cementedPlugs?.map(
                                                                                (c, idx) => {
                                                                                    if (idx !== cementIndex) return c

                                                                                    return {
                                                                                        ...cementedPlug,
                                                                                        startMD: {
                                                                                            ...cementedPlug?.startMD,
                                                                                            value: Number(
                                                                                                e?.target?.value
                                                                                            )
                                                                                        }
                                                                                    }
                                                                                }
                                                                            )
                                                                        }))
                                                                    }
                                                                    if (e?.target?.value === '') {
                                                                        e.persist()
                                                                        setSelectedEvent(prevstate => ({
                                                                            ...prevstate,
                                                                            cementedPlugs: prevstate?.cementedPlugs?.map(
                                                                                (c, idx) => {
                                                                                    if (idx !== cementIndex) return c

                                                                                    return {
                                                                                        ...cementedPlug,
                                                                                        startMD: {
                                                                                            ...cementedPlug?.startMD,
                                                                                            value: null
                                                                                        }
                                                                                    }
                                                                                }
                                                                            )
                                                                        }))
                                                                    }
                                                                }}
                                                                style={
                                                                    errors?.cementedPlugs?.[cementIndex]?.startMD
                                                                        ? { borderColor: '#D9534F' }
                                                                        : {}
                                                                }
                                                                placeholder={`0.00`}
                                                            />
                                                        </div>
                                                    }
                                                />
                                                <ModalField
                                                    label={'Final MD'}
                                                    labelAditionalField={
                                                        <CustomMeasureSelect
                                                            disabled
                                                            setSelectedUomValue={uomValue =>
                                                                setSelectedEvent(prevstate => ({
                                                                    ...prevstate,
                                                                    cementedPlugs: prevstate?.cementedPlugs?.map(
                                                                        (c, idx) => {
                                                                            if (idx !== cementIndex) return c

                                                                            return {
                                                                                ...cementedPlug,
                                                                                finalMD: uomValue
                                                                            }
                                                                        }
                                                                    )
                                                                }))
                                                            }
                                                            selectedOptionValues={cementedPlug?.finalMD}
                                                            options={unitOptions}
                                                        />
                                                    }
                                                    field={
                                                        <label className="input-uom">
                                                            <NumberInput
                                                                value={cementedPlug?.finalMD?.value}
                                                                onChange={e => {
                                                                    if (regexNumberAndComma.test(e?.target?.value)) {
                                                                        e.persist()
                                                                        setSelectedEvent(prevstate => ({
                                                                            ...prevstate,
                                                                            cementedPlugs: prevstate?.cementedPlugs?.map(
                                                                                (c, idx) => {
                                                                                    if (idx !== cementIndex) return c

                                                                                    return {
                                                                                        ...cementedPlug,
                                                                                        finalMD: {
                                                                                            ...cementedPlug?.finalMD,
                                                                                            value: Number(
                                                                                                e?.target?.value
                                                                                            )
                                                                                        }
                                                                                    }
                                                                                }
                                                                            )
                                                                        }))
                                                                    }
                                                                    if (e?.target?.value === '') {
                                                                        e.persist()
                                                                        setSelectedEvent(prevstate => ({
                                                                            ...prevstate,
                                                                            cementedPlugs: prevstate?.cementedPlugs?.map(
                                                                                (c, idx) => {
                                                                                    if (idx !== cementIndex) return c

                                                                                    return {
                                                                                        ...cementedPlug,
                                                                                        finalMD: {
                                                                                            ...cementedPlug?.finalMD,
                                                                                            value: null
                                                                                        }
                                                                                    }
                                                                                }
                                                                            )
                                                                        }))
                                                                    }
                                                                }}
                                                                style={
                                                                    errors?.cementedPlugs?.[cementIndex]?.finalMD
                                                                        ? { borderColor: '#D9534F' }
                                                                        : {}
                                                                }
                                                                placeholder={`0.00`}
                                                                disabled={
                                                                    !hasPermissionToEdit ||
                                                                    !cementedPlugPresent ||
                                                                    (fieldDisabledAfterEntityStart &&
                                                                        selectedEvent.type !== 'RISER')
                                                                }
                                                            />
                                                        </label>
                                                    }
                                                    style={{ width: '280px' }}
                                                />
                                                {cementedPlugPresent && selectedEvent.cementedPlugs.length > 1 && (
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                            <Tooltip style={{ zIndex: 9999 }} id={''}>
                                                                {i18n(
                                                                    'Delete this Cement Start MD and Cement Final MD'
                                                                )}
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <i
                                                            className="far fa-trash-alt remove-cemented-plug-btn"
                                                            onClick={() => {
                                                                if (
                                                                    hasPermissionToEdit ||
                                                                    !fieldDisabledAfterEntityStart
                                                                ) {
                                                                    setSelectedEvent(prevstate => ({
                                                                        ...prevstate,
                                                                        cementedPlugs: prevstate?.cementedPlugs?.filter(
                                                                            (_c, idx) => idx !== cementIndex
                                                                        )
                                                                    }))
                                                                }
                                                            }}
                                                        />
                                                    </OverlayTrigger>
                                                )}
                                            </FlexContainer>
                                        ))}
                                        <AddCementButton
                                            disabled={
                                                !hasPermissionToEdit ||
                                                !cementedPlugPresent ||
                                                fieldDisabledAfterEntityStart
                                            }
                                            onClick={addNewCementedPlug}
                                        >
                                            <i className="far fa-plus-circle" />
                                            {i18n('Add New Cemented Plug')}
                                        </AddCementButton>
                                    </CementContainer>
                                </ModalSideBody>
                            </div>
                        )}
                    </ScrollModalContainer>
                </>

                <ModalFooter liveTheme={context.liveTheme}>
                    {hasPermissionToEdit && (
                        <ModalButton
                            styleType="action"
                            onClick={() => {
                                const validation = validateFields(runRawEvent)
                                if (validation.pass) {
                                    setErrors(getEmptyErrors())
                                    postRun(runRawEvent).then(async response => {
                                        if (
                                            !runRawEvent.tubularsIntervalPerTime &&
                                            runRawEvent?.tubulars != null &&
                                            runRawEvent?.tubulars?.at(-1)?.finalMD?.value !==
                                                runRawEvent?.finalMD?.value
                                        ) {
                                            updateFinalMDValueOfLastTubular(runRawEvent)
                                        }

                                        if (tubulars && tubulars?.length > 0) {
                                            if (response?.id != undefined || response?.id != null) {
                                                const tubularsToUpdate = tubulars.map(tubular => ({
                                                    ...tubular,
                                                    run_id: response?.id
                                                }))

                                                if (typeof sharedState?.callback === 'function') {
                                                    sharedState.callback(tubularsToUpdate)
                                                }
                                                setTubulars(tubularsToUpdate)
                                            }
                                        }
                                        closeModal()
                                        reloadData()
                                    })
                                } else {
                                    setErrors(validation.errors)
                                }
                            }}
                        >
                            {i18n(btn)}
                        </ModalButton>
                    )}
                    <ModalButton styleType="cancel" onClick={closeModal}>
                        {hasPermissionToEdit ? i18n('Cancel') : i18n('Close')}
                    </ModalButton>
                </ModalFooter>
            </RunModalContainer>
        </>
    )
}

const RunModalContainer = styled.div`
    height: calc(100% - 80px);
    display: flex;
    flex-flow: column;
    justify-content: space-between;

    .input-uom {
        textarea {
            border-bottom-color: 1px solid ${({ theme }) => theme['input-borders']};
            border: 1px solid ${({ theme }) => theme['input-borders']};
            background-color: ${({ theme }) => theme['gray-lighter']};
            color: ${({ theme }) => theme['text-color']};
        }
    }
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
    }
    input[type='number'] {
        -moz-appearance: textfield;
    }
`

const AddCementButton = styled.div<{ disabled: boolean }>`
    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
    color: ${({ theme, disabled }) => theme[`blue-${disabled ? 300 : 500}`]};
    i {
        margin-right: 5px;
    }
`

const CementContainer = styled.div<{ showTrash: boolean; removeDisabledAfterEntityStart: boolean }>`
    display: flex;
    flex-flow: column;
    gap: 16px;
    height: auto;
    padding: 16px 12px;
    .remove-cemented-plug-btn {
        cursor: ${({ removeDisabledAfterEntityStart }) => (removeDisabledAfterEntityStart ? 'not-allowed' : 'pointer')};
        color: ${({ theme }) => theme['gray-400']};
        bottom: 13px;
        align-self: end;
        position: relative;
        :hover {
            color: ${({ theme, removeDisabledAfterEntityStart }) =>
                removeDisabledAfterEntityStart ? theme['gray-400'] : theme['red-700']};
        }
    }
    .modal-field-label {
        font-size: ${({ showTrash }) => (showTrash ? 12.3 : 13)}px;
    }
    .dropdown {
        button {
            font-size: ${({ showTrash }) => (showTrash ? 11.5 : 13)}px;
        }
    }
`
const FlexContainer = styled.div`
    display: flex;
    align-items: baseline;
    gap: 1rem;
`

const CustomMeasureWithTooltip = styled.div`
    display: flex;
    align-items: center;
    margin-left: 0.25rem;
`

const CheckBoxContainer = styled.div`
    .input-checkbox {
        display: flex;
        flex-direction: row;
        gap: 8px;
        align-items: center;
    }

    .input-checkbox input[type='checkbox']:not(.browser-default-input-style):checked::after {
        color: white !important;
        background-color: var(--blue-500);
    }
`
